import { BOOK_TITLES, type BookId } from "@/content/books";
import portrait from "@/assets/arda-event-portrait.webp.asset.json";

/**
 * Single source for the Turkish author area (/yazar, /hakkimda, /konusmalar):
 * biography facts, contributions, book blurbs and the Person schema, so the
 * pages never contradict each other.
 */
export const SITE = "https://akillisurdurulebilirlik.com";
export const AUTHOR_URL = `${SITE}/yazar`;
export const COLUMN_URL = "https://www.ekonomigazetesi.com/yazar/arda-oztaskin-34322";
export const LINKEDIN_URL = "https://www.linkedin.com/in/ardaoztaskin/";
export const EMAIL = { user: "ardaoztaskin", domain: "gmail.com" };

export type AuthorBook = {
  id: BookId;
  to: "/kitaplar/iklim-degil-insanlik-krizi" | "/kitaplar/yapay-zeka-gucun-iki-yuzu" | "/kitaplar/yalnizlik-as" | "/kitaplar/hayati-yasanir-kilan-bosluktur";
  title: string;
  year: string;
  publisher: string;
  blurb: string;
};

/** Publication order (oldest first) so the line of thought reads forward. */
export const AUTHOR_BOOKS: AuthorBook[] = [
  {
    id: "climate",
    to: "/kitaplar/iklim-degil-insanlik-krizi",
    title: BOOK_TITLES.climate.titleTr,
    year: BOOK_TITLES.climate.year,
    publisher: "Yeni İnsan Yayınevi",
    blurb: "İklim krizini emisyon tablolarının ötesinde, insanın kendi kurduğu düzenle ilişkisi üzerinden okuyor.",
  },
  {
    id: "ai",
    to: "/kitaplar/yapay-zeka-gucun-iki-yuzu",
    title: BOOK_TITLES.ai.titleTr,
    year: BOOK_TITLES.ai.year,
    publisher: "Destek Yayınları",
    blurb: "Yapay zekânın vaatlerini ve risklerini yan yana koyuyor; kararların kime devredildiğini soruyor.",
  },
  {
    id: "loneliness",
    to: "/kitaplar/yalnizlik-as",
    title: BOOK_TITLES.loneliness.titleTr,
    year: BOOK_TITLES.loneliness.year,
    publisher: "Ücretsiz PDF",
    blurb: "Yalnızlığın nasıl bir pazara dönüştüğünü ve bunun bedelini kimin ödediğini anlatıyor.",
  },
  {
    id: "space",
    to: "/kitaplar/hayati-yasanir-kilan-bosluktur",
    title: BOOK_TITLES.space.titleTr,
    year: BOOK_TITLES.space.year,
    publisher: "Destek Yayınları",
    blurb: "Her şeyin verimlilikle ölçüldüğü bir çağda ölçüye sığmayanları; güveni, muhakemeyi ve birlikte geçen zamanı savunuyor.",
  },
];

export type Role = { role: string; org: string; when?: string };

/** Confirmed current work (stated on the existing biography). */
export const CURRENT_WORK: Role[] = [
  { role: "Kurumsal İletişim yönetimi", org: "Yapı Kredi", when: "2018'den bu yana" },
  { role: "Sürdürülebilirlik İletişimi dersi", org: "Bahçeşehir Üniversitesi", when: "2023'ten bu yana" },
  { role: "Haftalık köşe yazısı, “Akıllı Sürdürülebilirlik”", org: "Nasıl Bir Ekonomi / Ekonomim" },
];

/** Board and association work — listed without a status until each is re-confirmed. */
export const BOARDS: Role[] = [
  { role: "Başkan Yardımcılığı", org: "Kurumsal İletişimciler Derneği (KİD)" },
  { role: "Danışma Kurulu üyeliği", org: "Bilgi Üniversitesi İletişim Fakültesi" },
  { role: "Danışma Kurulu üyeliği", org: "Sustainable Brands Türkiye" },
];

/** Career, newest first. Yapı Kredi is current and never shown as past. */
export const CAREER: Role[] = [
  { role: "Kurumsal İletişim", org: "Yapı Kredi", when: "2018 – bugün" },
  { role: "Marka Yönetimi Direktörü", org: "Yapı Kredi", when: "2008 – 2018" },
  { role: "Üst düzey iletişim görevleri", org: "Fortis Bank · Dışbank" },
  { role: "Reklam ve halkla ilişkiler", org: "Demirbank" },
];

export const EDUCATION: Role[] = [
  { role: "Lisans", org: "Ankara Üniversitesi Siyasal Bilgiler Fakültesi" },
  { role: "Lise", org: "TED Ankara Koleji" },
];

/** The single Person entity id used on every Turkish page. */
export const PERSON_ID = `${AUTHOR_URL}#person`;
/** Real author portrait (not the publication logo). */
export const PERSON_IMAGE = `${SITE}${portrait.url}`;
/** Verified profiles of the same person (each checked to resolve). */
export const SAME_AS = [
  LINKEDIN_URL,
  COLUMN_URL,
  "https://www.ekonomim.com/yazar/arda-oztaskin/1424",
  "https://smartsustainability.ai/",
];

/** Short reference for author/creator fields: links to the full Person node. */
export function personRef() {
  return { "@type": "Person", "@id": PERSON_ID, name: "Arda Öztaşkın", url: AUTHOR_URL };
}

export function personSchema() {
  return {
    "@type": "Person",
    "@id": PERSON_ID,
    name: "Arda Öztaşkın",
    alternateName: "Arda Oztaskin",
    url: AUTHOR_URL,
    image: PERSON_IMAGE,
    jobTitle: "Yazar",
    description: "İklim, yapay zekâ, ekonomi, yalnızlık ve anlam üzerine yazan yazar; dört kitabın yazarı.",
    knowsAbout: ["İklim krizi", "Yapay zekâ ve toplum", "Sürdürülebilirlik", "Sürdürülebilirlik iletişimi", "Yalnızlık ekonomisi"],
    alumniOf: { "@type": "CollegeOrUniversity", name: "Ankara Üniversitesi Siyasal Bilgiler Fakültesi" },
    worksFor: { "@type": "Organization", name: "Yapı Kredi" },
    sameAs: SAME_AS,
  };
}
